<template>
  <div class="station-selection" :class="darkMode ? 'dark' : ''">
    <h1 class="f4 title">{{ $t('Stations to launch the content on:') }}</h1>
    <div class="bookings">
      <h2 class="f5">{{ $t('Ongoing Sessions') }}</h2>
      <span class="f7" v-if="bookings.length === 0">{{ $t('Create and start a reservation or a walk-in to see stations here.') }}</span>
      <div class="booking" v-for="booking in bookings" :key="booking.id">
        <UICheckbox
          :title="booking.title"
          :value="getBookingCheckedState(booking)"
          :disabled="getBookingDisabledState(booking)"
          @change="selectBooking(booking)"
        />
        <div class="bookingstationtime" v-for="bookingStationTime in booking.bookingStationTimes" :key="bookingStationTime.id">
          <UICheckbox
            class="bookingstationtimeinput"
            :title="getBookingStationTimeTitle(bookingStationTime)"
            :disabled="getBookingStationTimeDisabledState(bookingStationTime)"
            :value="getBookingStationTimeCheckedState(bookingStationTime)"
            @change="selectBookingStationTime(bookingStationTime)"
          />
          <span v-if="getBookingStationTimeInstructions(bookingStationTime)" class="warningmessage f7">{{ getBookingStationTimeInstructions(bookingStationTime) }}</span>
        </div>
      </div>
    </div>
    <div class="seperator" v-if="stationsWithTimerDisabled.length"></div>
    <div class="otherstations" v-if="stationsWithTimerDisabled.length">
      <div>
        <span class="f5">{{ $t('More Available Stations') }}</span>
        <MoreInfoTooltip>
          <span class="f5" :class="darkMode ? 'darkbackground' : ''">{{ $t('The timer is disabled for these stations so they do not require an ongoing session to launch content.') }}</span>
        </MoreInfoTooltip>
      </div>
      <span class="f7" v-if="!stationsWithTimerDisabledAndNotInOnGoingSessions.length">{{ $t('All the stations with timer disabled are currently in ongoing sessions.') }}</span>
      <div class="experience" v-for="experience in experiences" :key="experience.id">
        <template v-if="getStationsWithTimerDisabledAndNotInOnGoingSessionInExperience(experience).length">
          <UICheckbox
              v-if="shouldExperienceCheckboxExist(experience)"
              class="otherexperience"
              :title="experience.title"
              :value="getExperienceCheckedState(experience)"
              :disabled="getExperienceDisabledState(experience)"
              @change="selectExperience(experience)"
            />
          <div class="otherexperience f8" v-else>{{ experience.title }}</div>
          <div class="otherstation"
            v-for="station in getStationsWithTimerDisabledAndNotInOnGoingSessionInExperience(experience)"
            :key="station.id"
          >
            <UICheckbox
              class="otherstationinput"
              :title="getStationTitle(station)"
              :value="getStationCheckedState(station)"
              :disabled="getStationDisabledState(station)"
              :darkMode="darkMode"
              @change="selectStation(station)"
            />
            <span v-if="getStationInstructions(station)" class="warningmessage f7">{{ getStationInstructions(station) }}</span>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import Component from 'vue-class-component';
import UICheckbox from '@/core/shared/components/ui/UICheckbox';
import MoreInfoTooltip from '@/core/shared/components/MoreInfoTooltip';

@Component({
  components: {
    UICheckbox,
    MoreInfoTooltip,
  },
  props: {
    bookings: {
      type: Array,
      required: true,
    },
    experiences: {
      type: Array,
      required: true,
    },
    stations: {
      type: Array,
      required: true,
    },
    darkMode: {
      type: Boolean,
      required: false,
    },
    stationsIdsWithOngoingSessions: {
      type: Array,
      required: false,
    },
    stationsWithTimerDisabled: {
      type: Array,
      required: false,
    },
    stationsIdsWithContentInstalled: {
      type: Array,
      required: false,
    },
    stationIdSelection: {
      type: Array,
      required: true,
    },
    contentSelection: {
      type: Object,
      required: false,
    },
  },
})
export default class StationSelection extends Vue {
  get notInstalledCopy () { return ` (${this.$t('Selected title not installed')})`; }
  get overAgeLimitCopy () { return this.$t('Selected title over station\'s age limit.'); }
  get alreadyPlayingCopy () { return this.$t('Already playing selected title.'); }

  /**
   * Booking
   */

  getBookingCheckedState (booking) {
    const disabledBookingStationTimes = [];
    const bookingChecked = booking.bookingStationTimes.every(bookingStationTime => {
      const checked = this.getBookingStationTimeCheckedState(bookingStationTime);
      const disabled = this.getBookingStationTimeDisabledState(bookingStationTime);
      if (disabled) disabledBookingStationTimes.push(disabled);
      return checked || disabled;
    });
    if (disabledBookingStationTimes.length === booking.bookingStationTimes.length) return false;
    return bookingChecked;
  }

  getBookingDisabledState (booking) {
    return booking.bookingStationTimes.every(bookingStationTime => {
      return this.getBookingStationTimeDisabledState(bookingStationTime);
    });
  }

  selectBooking (booking) {
    const newStationIdSelection = [...this.stationIdSelection];

    if (this.getBookingCheckedState(booking)) {
      booking.bookingStationTimes.forEach(bookingStationTime => {
        if (this.getBookingStationTimeCheckedState(bookingStationTime)) {
          newStationIdSelection.splice(newStationIdSelection.indexOf(bookingStationTime.station.id), 1);
        }
      });
    } else {
      booking.bookingStationTimes.forEach(bookingStationTime => {
        if (!this.getBookingStationTimeCheckedState(bookingStationTime) && !this.getBookingStationTimeDisabledState(bookingStationTime)) {
          newStationIdSelection.push(bookingStationTime.station.id);
        }
      });
    }

    this.$emit('update:stationIdSelection', newStationIdSelection);
  }

  /**
   * Booking Station Time
   */

  getBookingStationTimeTitle (bookingStationTime) {
    let title = `${bookingStationTime.station.title}`;
    if (this.stationsIdsWithContentInstalled && !this.doesStationHaveContentInstalled(bookingStationTime.station.id)) {
      title += this.notInstalledCopy;
    }
    return title;
  }

  getBookingStationTimeInstructions (bookingStationTime) {
    let message = '';

    if (!bookingStationTime._playing) {
      message += this.$t('Remember to start or add time.') + ' ';
    }

    if (this.stationsIdsWithContentInstalled && this.doesStationHaveContentInstalled(bookingStationTime.station.id)) {
      if (this.contentSelection && bookingStationTime.station.ageLimit > 0 && bookingStationTime.station.ageLimit < this.contentSelection.ageRating) {
        message += this.overAgeLimitCopy + ' ';
      }
      if (bookingStationTime.station.playing && this.contentSelection && bookingStationTime.station.playing.id === this.contentSelection.id) {
        message += this.alreadyPlayingCopy;
      }
    }

    return message;
  }

  getBookingStationTimeDisabledState (bookingStationTime) {
    if (this.stationsIdsWithContentInstalled && !this.doesStationHaveContentInstalled(bookingStationTime.station.id)) return true;
  }

  getBookingStationTimeCheckedState (bookingStationTime) {
    return this.stationIdSelection.includes(bookingStationTime.station.id);
  }

  selectBookingStationTime (bookingStationTime) {
    const newStationIdSelection = [...this.stationIdSelection];
    if (this.getBookingStationTimeCheckedState(bookingStationTime)) {
      newStationIdSelection.splice(newStationIdSelection.indexOf(bookingStationTime.station.id), 1);
    } else {
      newStationIdSelection.push(bookingStationTime.station.id);
    }
    this.$emit('update:stationIdSelection', newStationIdSelection);
  }

  /**
   * Experience
   */

  shouldExperienceCheckboxExist (experience) {
    return this.getStationsInExperience(experience).length === this.getStationsWithTimerDisabledAndNotInOnGoingSessionInExperience(experience).length;
  }

  getExperienceCheckedState (experience) {
    const experienceStations = this.getStationsWithTimerDisabledAndNotInOnGoingSessionInExperience(experience);
    const disabledStations = [];
    const experienceChecked = experienceStations.every(station => {
      const checked = this.getStationCheckedState(station);
      const disabled = this.getStationDisabledState(station);
      if (disabled) disabledStations.push(disabled);
      return checked || disabled;
    });
    if (disabledStations.length === experienceStations.length) return false;
    return experienceChecked;
  }

  getExperienceDisabledState (experience) {
    const experienceStations = this.getStationsWithTimerDisabledAndNotInOnGoingSessionInExperience(experience);
    return experienceStations.every(station => {
      return this.getStationDisabledState(station);
    });
  }

  selectExperience (experience) {
    const newStationIdSelection = [...this.stationIdSelection];
    const experienceStations = this.getStationsWithTimerDisabledAndNotInOnGoingSessionInExperience(experience);

    if (this.getExperienceCheckedState(experience)) {
      experienceStations.forEach(station => {
        if (this.getStationCheckedState(station)) {
          newStationIdSelection.splice(newStationIdSelection.indexOf(station.id), 1);
        }
      });
    } else {
      experienceStations.forEach(station => {
        if (!this.getStationCheckedState(station) && !this.getStationDisabledState(station)) {
          newStationIdSelection.push(station.id);
        }
      });
    }

    this.$emit('update:stationIdSelection', newStationIdSelection);
  }

  /**
   * Station
   */

  get stationsWithTimerDisabledAndNotInOnGoingSessions () {
    return this.stationsWithTimerDisabled
      .filter(station => !this.stationsIdsWithOngoingSessions.includes(station.id));
  }

  getStationsInExperience (experience) {
    return this.stations.filter(station => station.experience.id === experience.id);
  }

  getStationsWithTimerDisabledAndNotInOnGoingSessionInExperience (experience) {
    return this.stationsWithTimerDisabled
      .filter(station => !this.stationsIdsWithOngoingSessions.includes(station.id))
      .filter(station => station.experience.id === experience.id);
  }

  getStationTitle (station) {
    let title = station.title;
    if (this.stationsIdsWithContentInstalled && !this.doesStationHaveContentInstalled(station.id)) {
      title += this.notInstalledCopy;
    }
    return title;
  }

  getStationInstructions (station) {
    let message = '';
    if (this.contentSelection && station.ageLimit > 0 && station.ageLimit < this.contentSelection.ageRating) {
      message += this.overAgeLimitCopy + ' ';
    }
    if (station.playing && this.contentSelection && station.playing.id === this.contentSelection.id) {
      message += this.alreadyPlayingCopy;
    }
    return message;
  }

  getStationCheckedState (station) {
    return this.stationIdSelection.includes(station.id);
  }

  getStationDisabledState (station) {
    if (this.stationsIdsWithContentInstalled && !this.doesStationHaveContentInstalled(station.id)) return true;
  }

  doesStationHaveContentInstalled (stationId) {
    return this.stationsIdsWithContentInstalled.includes(stationId);
  }

  selectStation (station) {
    const newStationIdSelection = [...this.stationIdSelection];
    if (this.getStationCheckedState(station)) {
      newStationIdSelection.splice(newStationIdSelection.indexOf(station.id), 1);
    } else {
      newStationIdSelection.push(station.id);
    }
    this.$emit('update:stationIdSelection', newStationIdSelection);
  }
}
</script>

<style lang="postcss" scoped>
@import 'core/shared/styles';

.station-selection {
  display: grid;
  grid-gap: var(--spacingSm);

  @media (--tablet) {
    grid-template-columns: 1fr 1px 1fr;
    grid-gap: var(--spacingMd);

    & .title {
      grid-column: span 3;
    }
  }

  & .bookings,
  & .otherstations {
    display: flex;
    flex-direction: column;

    & > * {
      margin-bottom: var(--spacingSm);

      &:last-child {
        margin-bottom: 0;
      }
    }
  }

  & .booking,
  & .stations,
  & .experience {
    display: grid;
    grid-gap: var(--spacingXS);
  }

  & .bookingstationtime {
    display: flex;
    padding-left: var(--spacingSm);

    & .warningmessage {
      margin-left: var(--spacingSm);
      color: var(--colorErrorFlat);
    }
  }

  & .seperator {
    height: 1px;
    background-color: var(--colorGeyser);

    @media (--tablet) {
      height: 100%;
      justify-self: stretch;
      align-self: stretch;
    }
  }

  & .otherstations {
    & .otherstation {
      padding-left: var(--spacingSm);

      & .warningmessage {
        margin-left: var(--spacingSm);
        color: var(--colorErrorFlat);
      }
    }
  }
}

.experience .otherexperience {
  color: var(--darkModeColorText) !important;
}

.otherstationinput ::v-deep .title-class {
  color: var(--darkModeColorText) !important;
}

.ui-checkbox .box-and-title .title[data-v-f92d2d28] {
  color: var(--darkModeColorText) !important;
}

.darkbackground {
  color: var(--darkModeColorDarkBackground) !important;
}
</style>
